<script lang="ts">
  import Input from "../Inputs/Input.svelte";
  import AddressPredictionInput from "../Inputs/AddressPredictionInput.svelte";
  import TextArea from "../Inputs/TextArea.svelte";
  import ExpandableSection from "../ExpandableSection.svelte";
  import Checkbox from "../Inputs/Checkbox.svelte";
  import { createOrUpdateAddress } from "../../lib/helpers/userManager";
  import type { Address } from "../../lib/api/address";
  import BaseAddress from "./BaseAddress.svelte";
  import { getCopy } from "../../lib/stores/copy";
  import { navigateTo } from "../../lib/routing";
  import { NavigationType } from "../../lib/stores/session";
  import { addressesStore, unsetAddressForEdit, setMode } from "../../lib/stores/addresses";
  import { get } from "svelte/store";
  import {
    getDeliveryInstructions,
    getNoContact,
    buildDeliveryInstructions,
    getFormattedAddress
  } from "../../lib/helpers/address";
  import Button from "../Button.svelte";
  import type { SearchAddressPrediction } from "../../lib/api/address";
  import { settingsStore } from "../../lib/stores/settings";
  import { getCustomCopy } from "../../lib/helpers/userDetails";
  import { updateUserMetadata } from "../../lib/apiLoader/user";
  import { userStore } from "../../lib/stores/user";
  import { AddressValidator } from "../../lib/helpers/address";
  import countryList from "country-list";
  import Select from "../Inputs/Select.svelte";

  export let currentRoute;

  let aptUnitFloor = "";
  let deliveryInstructions = "";
  let selectedUserAddress: Address | null;
  let noContactDelivery = false;
  let isLoading = false;

  let address1 = "";
  let address2: string | undefined = undefined;
  let city = "";
  let state = "";
  let zip = "";
  let country = "";

  const { addressForEdit } = get(addressesStore);
  const countryOptions: { value: string; label: string }[] = countryList.getData().map((country: { code: string; name: string }) => ({
    value: country.code,
    label: country.name
  }));

  if (addressForEdit) {
    selectedUserAddress = addressForEdit;
    aptUnitFloor = addressForEdit.address2 || "";
    deliveryInstructions = getDeliveryInstructions(addressForEdit) || "";
    noContactDelivery = getNoContact(addressForEdit);
    address1 = addressForEdit.address1;
    address2 = addressForEdit.address2;
    city = addressForEdit.city;
    state = addressForEdit.state;
    zip = addressForEdit.zip;
    country = addressForEdit.country;
  }

  let fieldMessages: Record<string, string> = {};

  unsetAddressForEdit();

  const runValidations = () => {
    const { issues } = AddressValidator.validateAllFields({
      address1,
      address2,
      city,
      state,
      zip,
      country
    }, copy);
    fieldMessages = issues || {};
  };

  const onAddressSelected = (selectedAddress: SearchAddressPrediction | null) => {
    selectedUserAddress = selectedAddress
      ? {
          address1: selectedAddress.address1,
          city: selectedAddress.city,
          state: selectedAddress.state,
          zip: selectedAddress.zip,
          country: selectedAddress.country,
          latitude: selectedAddress.latitude,
          longitude: selectedAddress.longitude,
          formattedAddress: selectedAddress.formatted_address
        }
      : null;
  };

  const onNext = async () => {
    isLoading = true;

    if ($addressesStore.enterAddressManually) {
      runValidations();
      if (Object.values(fieldMessages).some((message) => message)) {
        isLoading = false;
        return;
      } else {
        selectedUserAddress = {
          address1,
          city,
          state,
          zip,
          country,
          // Fooji HQ coordinates
          // FIXME: This is just a temporary solution since we need the coordinates in the backend
          // A proper solution would be to try and figure out the coordinates in the backend, even if it's not 100% accurate
          // using Google Maps API or something similar
          latitude: 38.04,
          longitude: -84.49,
          formattedAddress: `${address1} ${address2} ${city} ${state} ${zip} ${country}`
        };
      }
    }

    const promises = [
      createOrUpdateAddress({
        ...(selectedUserAddress as Address),
        address2: $addressesStore.enterAddressManually ? address2 : aptUnitFloor,
        note: buildDeliveryInstructions(deliveryInstructions, noContactDelivery)
      })
    ];

    if (showTaxIdField) {
      promises.push(updateUserMetadata({ tax_id: $userStore.customFields.tax_id }));
    }

    // TODO: Try catch to handle failures
    await Promise.all(promises);
    navigateTo(NavigationType.Next);
    isLoading = false;
  };

  const onChooseFromSavedAddressesClick = () => setMode("select");
  const onEnterAddressManually = () => {
    $addressesStore.enterAddressManually = true;
    selectedUserAddress = null;
  };

  $: addressInvalid = $addressesStore.addressInvalid;
  $: noAddressesFound = $addressesStore.noAddressesFound;
  $: invalidMessage = noAddressesFound
    ? copy.noAddressesFoundText
    : addressInvalid
      ? copy.invalidAddressMessage
      : undefined;
  $: copy = $getCopy("address") as Record<string, string>;
  $: userCopy = $getCopy("user") as Record<string, string>;
  // In order to prevent the address placeholder from autocompleting, we add a zero-width space between each character
  $: addressPlaceholder = copy.addressPlaceholder.split("").join("\u200C");
  $: buttonCopy = selectedUserAddress?.id ? copy.updateAddressButtonText : copy.newAddressButtonText;
  $: [taxIdCustomField] = ($settingsStore.custom_fields ?? []).filter((field) => field.name === "tax_id");
  $: showTaxIdField = taxIdCustomField && (selectedUserAddress?.country || country) && [
    'AR', // Argentina
    'BR', // Brazil
    'CL', // Chile
    'CN', // China
    'IN', // India
    'MX', // Mexico
    'KR', // South Korea
    'TR', // Turkey
    'VN', // Vietnam
    'IL', // Israel
    'ID', // Indonesia
    'NO', // Norway
    'PE', // Peru
    'QA', // Qatar
    'ZA'  // South Africa
  ].includes(selectedUserAddress?.country || country);
  $: taxId = $userStore.customFields.tax_id;
  $: isValid = (Boolean(selectedUserAddress) && (showTaxIdField ? Boolean(taxId && String(taxId).length > 0) : true)) || $addressesStore.enterAddressManually;
</script>

<BaseAddress {currentRoute} {onNext} {selectedUserAddress} {buttonCopy} {isLoading} {isValid}>
  <svelte:fragment slot="content">
    <div class="flex flex-col gap-4">
      {#if !$addressesStore.enterAddressManually}
        <AddressPredictionInput
          placeholder={addressPlaceholder}
          {onAddressSelected}
          {invalidMessage}
          {onEnterAddressManually}
          value={selectedUserAddress ? getFormattedAddress(selectedUserAddress) : ""} />
        <Input placeholder={copy.address2Placeholder} bind:value={aptUnitFloor} />
      {/if}

      {#if $addressesStore.enterAddressManually}
        <div class="flex flex-col gap-4">
          <div class="flex">
            <button class="underline decoration-solid" on:click={() => {
              $addressesStore.enterAddressManually = false;
            }}>Use Address Search</button>
          </div>
          <div class="flex flex-col gap-2">
            <Select placeholder={copy.countryPlaceholder} bind:value={country} invalidMessage={fieldMessages.country} optionList={countryOptions} />
            <Input placeholder={copy.address1Placeholder} bind:value={address1} invalidMessage={fieldMessages.address1} />
            <Input placeholder={copy.address2Placeholder} bind:value={address2} invalidMessage={fieldMessages.address2} />
            <Input placeholder={copy.cityPlaceholder} bind:value={city} invalidMessage={fieldMessages.city} />
            <Input placeholder={copy.statePlaceholder} bind:value={state} invalidMessage={fieldMessages.state} />
            <Input placeholder={copy.zipPlaceholder} bind:value={zip} invalidMessage={fieldMessages.zip} />
          </div>
        </div>
      {/if}

      {#if showTaxIdField}
        <Input
          bind:value={$userStore.customFields.tax_id}
          placeholder={userCopy[`customField-${taxIdCustomField.identifier}Copy`] ?? taxIdCustomField.name}
          invalidMessage={getCustomCopy(taxIdCustomField, taxId ? undefined : "missing", userCopy)} />
      {/if}
      {#if !copy.disableNote}
        <ExpandableSection title={copy.notePlaceholder}>
          <TextArea placeholder={copy.notePlaceholder} bind:value={deliveryInstructions} />
        </ExpandableSection>
      {/if}
      {#if !copy.disableNoContact}
        <Checkbox label={copy.noContactDelivery} bind:value={noContactDelivery} />
      {/if}
    </div>
  </svelte:fragment>
  <svelte:fragment slot="additional-action-buttons">
    {#if $addressesStore.addresses.length > 0}
      <Button on:click={onChooseFromSavedAddressesClick} secondary>
        {copy.chooseFromSavedAddressesButtonText}
      </Button>
    {/if}
  </svelte:fragment>
</BaseAddress>
